/**
 * «Записи» catalogue, part 5 — the «Подсказчик» coach inside a game (owner 27.09: «поставь все фразы на озвучку»): the
 * hint ladder, threat warnings, the thinking routine, «Почему так?» (explain best), the opening idea, the teacher's
 * fallback take-back reminder — square-free (the board shows every square: zones, highlights, arrows), one line per
 * meaning, every sentence a whole line of a twin (./twins.ts; the builders are in ../events.ts). Placeholders: see
 * ./catalog.ts.
 *
 * A builder's text is exactly the chosen wordings joined (events.voice.test.ts measures every variant): a wording's
 * number is its recording's key, so wordings are only ever APPENDED to a line. When the bubble's own wording has no take
 * the planner says another wording of the same line (they are interchangeable) or the line's `fallback`.
 *
 * The move of a hint's green arrow and the better move of «Почему так?» are one line per move phrase («конь бьёт
 * пешку», «короткая рокировка», «ход слоном» — 42 of them, `HELPER_MOVE_PHRASES`): its wordings are the heads both
 * moments share («Смотри на зелёную стрелку: …», «Я бы сыграл так: …», «Показываю стрелкой: …»); «Сильнее было так: …»
 * is the post-mortem's own line (a hint is no «было»). The opening ideas of @gambit/content are told in a short spoken
 * form (≤ 12 words: in-game speech stays short) — `HELPER_OPENINGS` mirrors the repertoire by line id and title
 * (catalog.helper.test.ts checks it against the real content).
 */
import type { ClipCatalogLine, ClipWording, MotifId, PieceType } from '@gambit/shared';
import { pieceNameRu } from '../spoken.ts';

const w = (...texts: string[]): ClipWording[] => texts.map((t) => ({ t }));

/** Every piece, the king too: a hint's piece or move may be the king's (castling, an endgame king, a king capture). */
const ALL_PIECES: readonly PieceType[] = ['p', 'n', 'b', 'r', 'q', 'k'];
/** What can be captured (never the king). */
const TAKEN: readonly PieceType[] = ['p', 'n', 'b', 'r', 'q'];

// ───────────────────────── hint ladder, level 1: a lead-in and a Socratic question per idea ─────────────────────────

const HINT_L1: readonly ClipCatalogLine[] = [
  // (the lead-in may be none: the question alone)
  { id: 'helper.hint.lead', role: 'whole', wordings: w('Давай вместе.', 'Так-так-так…', 'Подумаем!', 'Хороший момент, чтобы подумать.') },
  { id: 'helper.hint.q.mate', role: 'whole', wordings: w('Проверь все шахи: нет ли среди них мата?', 'Посмотри на короля соперника: куда он может убежать?') },
  { id: 'helper.hint.q.captureFree', role: 'whole', wordings: w('Какая фигура соперника осталась без защиты?', 'Что у соперника можно забрать бесплатно?') },
  { id: 'helper.hint.q.capture', role: 'whole', wordings: w('Какие взятия у тебя есть — и какое самое выгодное?', 'Проверь все взятия: кто что заберёт в ответ?') },
  { id: 'helper.hint.q.check', role: 'whole', wordings: w('Какие шахи у тебя есть?', 'Что будет, если побеспокоить короля соперника?') },
  { id: 'helper.hint.q.saveHanging', role: 'whole', wordings: w('Какая твоя фигура сейчас под боем?', 'Всем ли твоим фигурам спокойно?') },
  { id: 'helper.hint.q.defend', role: 'whole', wordings: w('Что хочет соперник — на что он напал?', 'Какой твоей фигуре нужна помощь?') },
  { id: 'helper.hint.q.castle', role: 'whole', wordings: w('Безопасно ли твоему королю в центре?', 'Как спрятать короля и разбудить ладью одним ходом?') },
  { id: 'helper.hint.q.promotion', role: 'whole', wordings: w('Какая твоя пешка ближе всех к превращению?', 'Кто мешает твоей пешке добежать до конца?') },
  { id: 'helper.hint.q.develop', role: 'whole', wordings: w('Какие твои фигуры ещё спят дома?', 'Кого пора разбудить и вывести в игру?') },
  { id: 'helper.hint.q.quiet', role: 'whole', wordings: w('Что хочет соперник, и какая твоя фигура стоит хуже всех?', 'Какие у тебя есть шахи, взятия и угрозы?') },
  // no engine line to lean on: one general question
  {
    id: 'helper.hint.ask',
    role: 'whole',
    wordings: w('Что хочет соперник?', 'Какие у тебя есть шахи, взятия и угрозы?', 'Какой ход тебе нравится — и безопасен ли он?', 'Назови два хода, которые тебе нравятся, и выбери лучший.'),
  },
];

// ───────────────────────── level 2: the yellow zone, then what is there ─────────────────────────

/** The four sentences of a zone (`Посмотри на {ZA} — …`, `Самое интересное сейчас — {ZL}.`, …) for one side of the board. */
function zoneLine(zone: 'kingside' | 'queenside' | 'center', acc: string, loc: string): ClipCatalogLine {
  return {
    id: `helper.hint.zone.${zone}`,
    role: 'whole',
    wordings: w(`Посмотри на ${acc} — я подсветил клетки жёлтым.`, `Самое интересное сейчас — ${loc}.`, `Подсказываю место: ${acc}.`, `Ищи ${loc}, где жёлтые клетки.`),
  };
}

const HINT_L2: readonly ClipCatalogLine[] = [
  zoneLine('kingside', 'королевский фланг', 'на королевском фланге'),
  zoneLine('queenside', 'ферзевый фланг', 'на ферзевом фланге'),
  zoneLine('center', 'центр доски', 'в центре доски'),
  { id: 'helper.hint.nudge.mate', role: 'whole', wordings: w('Там можно поймать короля.') },
  { id: 'helper.hint.nudge.captureFree', role: 'whole', wordings: w('Там есть что забрать.') },
  { id: 'helper.hint.nudge.capture', role: 'whole', wordings: w('Там есть интересное взятие.') },
  { id: 'helper.hint.nudge.check', role: 'whole', wordings: w('Там прячется шах.') },
  // a hanging piece to save, a piece to defend
  { id: 'helper.hint.nudge.help', role: 'whole', wordings: w('Там твоей фигуре нужна помощь.') },
  { id: 'helper.hint.nudge.castle', role: 'whole', wordings: w('Там королю будет спокойнее.') },
  { id: 'helper.hint.nudge.promotion', role: 'whole', wordings: w('Там пешка рвётся вперёд.') },
  { id: 'helper.hint.nudge.develop', role: 'whole', wordings: w('Туда хорошо вывести фигуру.') },
  { id: 'helper.hint.nudge.quiet', role: 'whole', wordings: w('Там можно усилить позицию.') },
];

// ───────────────────────── level 3: the blue piece, then what it can do ─────────────────────────

/**
 * The tail after the piece names it by a pronoun («Он может дать шах — куда?»); each tail line's plain wording names
 * nothing, so it can stand in after any head. A castle is always the king's, a promotion the pawn's.
 */
const HINT_L3: readonly ClipCatalogLine[] = [
  {
    id: 'helper.hint.piece',
    role: 'whole',
    byPiece: ALL_PIECES,
    wordings: w('Посмотри на {своего} {коня}.', 'Подсказываю фигуру: {твой} {конь}.', 'Главный герой этого хода — {твой} {конь}.', 'Я подсветил синим {твоего} {коня}.', 'Смотри, что я подсветил синим!'),
  },
  { id: 'helper.hint.tail.mate', role: 'whole', byPiece: ALL_PIECES, wordings: w('{Он} может поставить мат — найдёшь как?', 'Тут можно поставить мат — найдёшь как?') },
  { id: 'helper.hint.tail.capture', role: 'whole', byPiece: ALL_PIECES, wordings: w('{Он} может кое-что забрать — найдёшь что?', 'Тут можно кое-что забрать — найдёшь что?') },
  { id: 'helper.hint.tail.check', role: 'whole', byPiece: ALL_PIECES, wordings: w('{Он} может дать шах — куда?', 'Тут можно дать шах — куда?') },
  { id: 'helper.hint.tail.saveHanging', role: 'whole', byPiece: true, wordings: w('{Он} под боем — куда {ему} лучше уйти?', 'Фигура под боем — куда ей лучше уйти?') },
  { id: 'helper.hint.tail.castle', role: 'whole', wordings: w('Как спрятать его в безопасный домик?') },
  { id: 'helper.hint.tail.promotion', role: 'whole', wordings: w('Она совсем близко к превращению!') },
  { id: 'helper.hint.tail.develop', role: 'whole', byPiece: ['n', 'b'], wordings: w('{Он} ещё не {p:вышел|вышла} в игру — куда {его} поставить?', 'Фигура ещё не вышла в игру — куда её поставить?') },
  { id: 'helper.hint.tail.defend', role: 'whole', byPiece: ALL_PIECES, wordings: w('{Он} может помочь другой твоей фигуре — как?', 'Можно помочь другой твоей фигуре — как?') },
  { id: 'helper.hint.tail.quiet', role: 'whole', byPiece: ALL_PIECES, wordings: w('Куда {ему} лучше пойти?', 'Куда лучше пойти этой фигурой?') },
];

// ───────────────────────── level 4 and «Почему так?»: the move of the green arrow ─────────────────────────

/** The move phrases a hint or «Почему так?» names (`moveWordsRu` of ../events.ts): its key is a line id's tail. */
export interface HelperMovePhrase {
  key: string;
  phrase: string;
}

export const HELPER_MOVE_PHRASES: readonly HelperMovePhrase[] = [
  { key: 'castle.short', phrase: 'короткая рокировка' },
  { key: 'castle.long', phrase: 'длинная рокировка' },
  ...(['q', 'r', 'b', 'n'] as const).map((p) => ({ key: `promo.${p}`, phrase: `пешка превращается в ${pieceNameRu(p, 'acc')}` })),
  ...ALL_PIECES.flatMap((p) => TAKEN.map((c) => ({ key: `take.${p}.${c}`, phrase: `${pieceNameRu(p, 'nom')} бьёт ${pieceNameRu(c, 'acc')}` }))),
  ...ALL_PIECES.map((p) => ({ key: `go.${p}`, phrase: `ход ${pieceNameRu(p, 'ins')}` })),
];

/**
 * The heads a hint (level 4) and «Почему так?» share: both show the move by the green arrow, so any of them may stand
 * in for another. `helper.move.<key>` = these heads before the move phrase.
 */
export const HELPER_MOVE_HEADS: readonly string[] = ['Смотри на зелёную стрелку:', 'Я бы сыграл так:', 'Показываю стрелкой:'];

/** «Почему так?»'s own head (the move was not played: «было»); `helper.explain.best.<key>`, falling back to the shared line. */
export const HELPER_EXPLAIN_HEAD = 'Сильнее было так:';

/** The better move of «Почему так?» when it is not a legal move of that position (a broken judgement): the arrow shows it. */
export const HELPER_EXPLAIN_ARROW_TEXT = `${HELPER_EXPLAIN_HEAD} ход по зелёной стрелке.`;

const MOVES: readonly ClipCatalogLine[] = [
  ...HELPER_MOVE_PHRASES.map((m): ClipCatalogLine => ({ id: `helper.move.${m.key}`, role: 'whole', wordings: w(...HELPER_MOVE_HEADS.map((h) => `${h} ${m.phrase}.`)) })),
  ...HELPER_MOVE_PHRASES.map((m): ClipCatalogLine => ({ id: `helper.explain.best.${m.key}`, role: 'whole', fallback: `helper.move.${m.key}`, wordings: w(`${HELPER_EXPLAIN_HEAD} ${m.phrase}.`) })),
  { id: 'helper.explain.best.arrow', role: 'whole', wordings: w(HELPER_EXPLAIN_ARROW_TEXT) },
];

/** Level 4: why the move is good (after the move sentence) — two ways to say each reason. */
const HINT_L4: readonly ClipCatalogLine[] = [
  { id: 'helper.hint.why.mate', role: 'whole', wordings: w('Это мат!', 'И это сразу мат!') },
  { id: 'helper.hint.why.mateSoon', role: 'whole', wordings: w('Так ты идёшь прямо к мату.', 'Это дорога к мату!') },
  { id: 'helper.hint.why.captureFree', role: 'whole', wordings: w('Фигура соперника стояла без защиты.', 'Эту фигуру соперника никто не защищал.') },
  { id: 'helper.hint.why.capture', role: 'whole', wordings: w('Это выгодное взятие.', 'Так ты забираешь больше, чем отдаёшь.') },
  { id: 'helper.hint.why.check', role: 'whole', wordings: w('Это шах, и сопернику придётся защищаться.', 'Шах — и соперник должен спасать короля.') },
  { id: 'helper.hint.why.saveHanging', role: 'whole', byPiece: true, wordings: w('Так {конь} уходит из-под боя.', 'Так фигура уходит из-под боя.') },
  { id: 'helper.hint.why.castle', role: 'whole', wordings: w('Король прячется в домик, а ладья выходит в игру.', 'Король в безопасности, а ладья в игре!') },
  { id: 'helper.hint.why.promotion', role: 'whole', wordings: w('Так у тебя появится новая фигура!', 'Вот и новая фигура!') },
  { id: 'helper.hint.why.develop', role: 'whole', wordings: w('Ещё одна фигура выходит в игру.', 'Ещё одна фигура просыпается!') },
  { id: 'helper.hint.why.defend', role: 'whole', wordings: w('Так все твои фигуры под защитой.', 'Так ты защищаешь свою фигуру.') },
  { id: 'helper.hint.why.quiet', role: 'whole', wordings: w('Этот ход делает твою позицию крепче.', 'Спокойный и крепкий ход.') },
];

// ───────────────────────── «Почему так?» (explain best) ─────────────────────────

/**
 * The second sentence of «Почему так?»: what the child's move allowed (`ALLOWED_AFTER` of ../brief.ts as a sentence),
 * else what the better move was (a motif). Keys: the motif, 'mate' (a mate threat of any length), 'noReply' (no
 * refutation known), 'reply' (the red arrow shows the answer).
 */
export const HELPER_EXPLAIN_ALLOWED: Readonly<Record<string, string>> = {
  hangingPiece: 'А после твоего хода фигура осталась без защиты.',
  freeCapture: 'А после твоего хода соперник забирает фигуру бесплатно.',
  badTrade: 'А после твоего хода размен получается невыгодным.',
  fork: 'А после твоего хода соперник ставит вилку.',
  pin: 'А после твоего хода получается связка.',
  skewer: 'А после твоего хода соперник наносит сквозной удар.',
  discoveredAttack: 'А после твоего хода соперник открывает нападение.',
  doubleCheck: 'А после твоего хода соперник даёт двойной шах.',
  removeDefender: 'А после твоего хода соперник убирает защитника.',
  trappedPiece: 'А после твоего хода фигуре некуда отступить.',
  backRankMate: 'А после твоего хода появляется угроза мата на последней линии.',
  mate: 'А после твоего хода появляется угроза мата.',
  promotion: 'А после твоего хода пешка соперника бежит к превращению.',
  kingSafety: 'А после твоего хода королю становится опасно.',
  development: 'А после твоего хода фигуры остаются дома.',
  center: 'А после твоего хода центр достаётся сопернику.',
  noReply: 'А твой ход давал сопернику больше шансов.',
  reply: 'А после твоего хода у соперника сильный ответ — красная стрелка.',
};

/** The idea of the better move, as its own sentence (the green arrow shows the move). */
export const HELPER_EXPLAIN_MISSED: Readonly<Record<MotifId, string>> = {
  hangingPiece: 'Фигура соперника стояла без защиты.',
  freeCapture: 'Это бесплатное взятие.',
  badTrade: 'Это выгодный размен.',
  fork: 'Это вилка!',
  pin: 'Это связка!',
  skewer: 'Это сквозной удар!',
  discoveredAttack: 'Это вскрытое нападение!',
  doubleCheck: 'Это двойной шах!',
  removeDefender: 'Так убирается защитник.',
  trappedPiece: 'Так ловится фигура соперника.',
  backRankMate: 'Это мат на последней линии!',
  mateIn1: 'Это мат!',
  mateIn2: 'Это мат в два хода!',
  mateIn3: 'Это мат в три хода!',
  promotion: 'Пешка бежит к превращению.',
  kingSafety: 'Так королю спокойнее.',
  development: 'В игру выходит ещё одна фигура.',
  center: 'Так ты занимаешь центр.',
};

const EXPLAIN: readonly ClipCatalogLine[] = [
  // the child's own move was the engine's first line
  {
    id: 'helper.explain.found',
    role: 'whole',
    byGender: true,
    wordings: w(
      'Ты {g:нашёл|нашла} самый сильный ход в этой позиции!',
      'Так и надо было — сильнее тут ничего нет!',
      'Сильнее хода тут нет — ты {g:выбрал|выбрала} его {g:сам|сама}!',
      'Зелёная стрелка — это твой ход. Ты {g:нашёл|нашла} его {g:сам|сама}!',
    ),
  },
  ...Object.entries(HELPER_EXPLAIN_ALLOWED).map(([key, t]): ClipCatalogLine => ({ id: `helper.explain.allowed.${key}`, role: 'whole', wordings: w(t) })),
  ...Object.entries(HELPER_EXPLAIN_MISSED).map(([key, t]): ClipCatalogLine => ({ id: `helper.explain.missed.${key}`, role: 'whole', wordings: w(t) })),
];

// ───────────────────────── threat warnings ─────────────────────────

/**
 * The three sentences of a tactic the opponent prepares (an engine-found threat, no piece of the child hangs yet): its
 * title (`motifTitleInlineRu`), its accusative (the «готовит …» form). `first` replaces the first sentence where it
 * would run over the 80-character cap («— кажется, уничтожение защитника! Что он задумал?»).
 */
function tactic(title: string, acc: string, first?: string): readonly string[] {
  return [first ?? `Осторожно: соперник что-то готовит — кажется, ${title}! Что он задумал?`, `Копытом чую — у соперника идея: ${title}. Найдёшь её?`, `Что хочет соперник? Похоже, он готовит ${acc}.`];
}

/**
 * The motifs the threat search can name (../threats.ts: `describeMotif`, else a hanging piece) → the threat warning's
 * sentences (the builder picks one; `helper.threat.tactic.<motif>`). Any other motif says `HELPER_THREAT_TACTIC_ANY`.
 */
export const HELPER_THREAT_TACTICS: Readonly<Partial<Record<MotifId, readonly string[]>>> = {
  hangingPiece: tactic('фигура без защиты', 'ловушку'),
  freeCapture: tactic('бесплатное взятие', 'ловушку'),
  fork: tactic('вилка', 'вилку'),
  pin: tactic('связка', 'связку'),
  skewer: tactic('сквозной удар', 'сквозной удар'),
  discoveredAttack: tactic('вскрытое нападение', 'вскрытое нападение', 'Осторожно: кажется, соперник готовит вскрытое нападение! Что он задумал?'),
  doubleCheck: tactic('двойной шах', 'двойной шах'),
  removeDefender: tactic('уничтожение защитника', 'размен защитника', 'Осторожно: кажется, соперник готовит размен защитника! Что он задумал?'),
  trappedPiece: tactic('ловля фигуры', 'ловушку для фигуры'),
  promotion: tactic('превращение пешки', 'превращение пешки'),
};

/** A threat of a motif without its own sentences (`helper.threat.tactic`; also every tactic line's L3 stand-in). */
export const HELPER_THREAT_TACTIC_ANY: readonly string[] = ['Осторожно: соперник что-то готовит! Что он задумал?', 'Копытом чую — у соперника есть идея. Найдёшь её?', 'Что хочет соперник? Похоже, он готовит ловушку.'];

const THREATS: readonly ClipCatalogLine[] = [
  // stage 4+: only asks — no piece, no marks
  {
    id: 'helper.threat.ask',
    role: 'whole',
    wordings: w('Что хочет соперник? Проверь, всем ли твоим фигурам спокойно.', 'Перед ходом — быстрая проверка: что у тебя под боем?', 'Копытом чую — соперник что-то задумал. Найдёшь его угрозу?', 'Одной твоей фигуре сейчас неуютно. Найдёшь какой?'),
  },
  {
    id: 'helper.threat.mate',
    role: 'whole',
    wordings: w('Осторожно: соперник грозит матом! Как защитить короля?', 'Стоп, посмотри на своего короля: соперник хочет поставить мат. Что делать?', 'Копытом чую опасность: у соперника угроза мата! Где спрятать короля?'),
  },
  // (the sentence names the piece, the red mark shows which one; a pronoun only ever follows the noun in one wording)
  {
    id: 'helper.threat.piece',
    role: 'whole',
    byPiece: true,
    fallback: 'helper.threat.ask',
    wordings: w(
      'Осторожно: {твой} {конь} под боем! Кто {его} защищает?',
      'Прежде чем ходить, глянь на {своего} {коня}. Спокойно ли {ему} там?',
      'Копытом чую: на {твоего} {коня} кто-то напал! Как {ему} помочь?',
      'Что хочет соперник? Посмотри на {своего} {коня} — я подсветил красным.',
      'Посмотри на {своего} {коня} — что задумал соперник?',
    ),
  },
  { id: 'helper.threat.tactic', role: 'whole', wordings: w(...HELPER_THREAT_TACTIC_ANY) },
  ...Object.entries(HELPER_THREAT_TACTICS).map(([motif, texts]): ClipCatalogLine => ({ id: `helper.threat.tactic.${motif}`, role: 'whole', fallback: 'helper.threat.tactic', wordings: w(...(texts ?? [])) })),
];

// ───────────────────────── the thinking routine ─────────────────────────

/** A lead-in, then the routine; two questions up to stage 2, three from stage 3 (different meanings: different lines). */
const ROUTINE: readonly ClipCatalogLine[] = [
  { id: 'helper.routine.lead', role: 'whole', wordings: w('Сначала смотрим — потом ходим!') },
  { id: 'helper.routine.lead.two', role: 'whole', fallback: 'helper.routine.lead', wordings: w('Помни наш секрет — два вопроса перед каждым ходом.', 'Два вопроса мастера.', 'Давай вспомним два вопроса.') },
  { id: 'helper.routine.lead.three', role: 'whole', fallback: 'helper.routine.lead', wordings: w('Помни наш секрет — три вопроса перед каждым ходом.', 'Три вопроса мастера.', 'Давай вспомним три вопроса.') },
  { id: 'helper.routine.two', role: 'whole', wordings: w('Что хочет соперник? Мой ход безопасен?') },
  { id: 'helper.routine.three', role: 'whole', wordings: w('Что хочет соперник? Что могу я — шахи, взятия, угрозы? Безопасно ли?') },
];

// ───────────────────────── the opening idea (the repertoire of @gambit/content) ─────────────────────────

/**
 * The repertoire lines of @gambit/content (`OPENING_REPERTOIRE[].lines`): id, title, a cautionary line, and the idea
 * as Гамбитик says it in a game — ≤ 12 words (the content's own idea is one or two long sentences for the curriculum
 * screen). catalog.helper.test.ts keeps ids, titles and warnings equal to the content.
 */
export const HELPER_OPENINGS: readonly { id: string; title: string; idea: string; warning?: boolean }[] = [
  { id: 'italian-quiet', title: 'Тихая итальянка', idea: 'Никаких ловушек: развиваем все фигуры, прячем короля и готовим удар в центре.' },
  { id: 'italian-strong-center', title: 'Сильный центр', idea: 'Строим две пешки в центре — после размена у фигур открываются линии.' },
  { id: 'italian-two-knights-quiet', title: 'Если чёрные вывели второго коня', idea: 'Спокойно защищаем центральную пешку — получается та же тихая итальянка.' },
  { id: 'white-vs-sicilian', title: 'Против сицилианской защиты', idea: 'Тот же план «сильный центр»: готовим шаг ферзевой пешки на две клетки.' },
  { id: 'white-vs-french', title: 'Против французской защиты', idea: 'Меняемся в центре и получаем открытую игру с простым развитием.' },
  { id: 'white-vs-scandinavian', title: 'Против скандинавской защиты', idea: 'Бьём пешку, а потом конь выходит с нападением на ферзя.' },
  { id: 'black-vs-italian', title: 'Против итальянской партии', idea: 'Развиваемся зеркально: слон, конь, скромный шаг ферзевой пешки и рокировка.' },
  { id: 'black-vs-italian-center', title: 'Белые строят центр — бьём по нему', idea: 'Меняем пешку, даём шах слоном и бьём по центру ферзевой пешкой.' },
  { id: 'black-vs-knight-attack', title: 'Против выпада конём: без «жареной печени»', idea: 'Пешку конём не берём — уводим коня на край с нападением на слона.' },
  { id: 'black-vs-early-queen', title: 'Против раннего ферзя', idea: 'Защищаем пешку, закрываем угрозу мата и развиваемся, пока ферзь бегает.' },
  { id: 'black-vs-spanish', title: 'Против испанской партии', idea: 'Прогоняем слона пешками, развиваем фигуры и делаем рокировку.' },
  { id: 'black-vs-scotch', title: 'Против шотландской партии', idea: 'Бьём пешку в центре и развиваемся: каждый наш ход что-то атакует.' },
  { id: 'black-qgd-setup', title: 'Основная расстановка', idea: 'Пешка в центре под защитой, конь, слон, рокировка — потом удар по центру.' },
  { id: 'black-vs-london', title: 'Против лондонской системы', idea: 'Слон белых ушёл, и пешка рядом ослабла — нападаем на неё ферзём.' },
  { id: 'black-vs-flank', title: 'Против фланговых начал', idea: 'Та же расстановка: пешки в центр, конь, слон, рокировка.' },
  { id: 'black-greedy-pawn-trap', title: 'Почему нельзя жадничать', idea: 'Чёрные держатся за лишнюю пешку и теряют ладью в углу.', warning: true },
];

/** The title as the head says it: «Тихая итальянка.» → «тихая итальянка». */
export function openingTitleSpoken(title: string): string {
  const t = title.trim().replace(/[.!?…]+$/u, '');
  return t.charAt(0).toLowerCase() + t.slice(1);
}

/** The heads before the title: a plan the child follows, and a cautionary line. */
export const HELPER_OPENING_HEADS: readonly ((title: string) => string)[] = [
  (title) => `Узнаю наш дебютный план: ${title}.`,
  (title) => `Это знакомый план: ${title}.`,
  (title) => `Копытом чую знакомый дебют: ${title}.`,
];
export const HELPER_OPENING_WARNING_HEADS: readonly ((title: string) => string)[] = [(title) => `Осторожно, знакомая история: ${title}.`, (title) => `Помнишь наш урок — ${title}?`];

/** The repertoire line of a title (as the game passes it: `lineTitle`), or undefined. */
export function helperOpeningOf(title: string): (typeof HELPER_OPENINGS)[number] | undefined {
  const key = openingTitleSpoken(title);
  return HELPER_OPENINGS.find((o) => openingTitleSpoken(o.title) === key);
}

const OPENINGS: readonly ClipCatalogLine[] = HELPER_OPENINGS.flatMap((o): ClipCatalogLine[] => [
  { id: `helper.opening.${o.id}`, role: 'whole', wordings: w(...(o.warning ? HELPER_OPENING_WARNING_HEADS : HELPER_OPENING_HEADS).map((h) => h(openingTitleSpoken(o.title)))) },
  { id: `helper.opening.${o.id}.idea`, role: 'whole', wordings: w(o.idea) },
]);

// ───────────────────────── the teacher's fallback take-back: the advice, by its piece ─────────────────────────

/**
 * The advised move of a take-back reminder, by its piece (`adviceWordsRu` of ../events.ts for one advice): castling,
 * a capture, a move — never its square (the arrows come back after «Верну ход»).
 */
export const HELPER_ADVICE_PHRASES: readonly HelperMovePhrase[] = [
  { key: 'castle', phrase: 'рокировка' },
  ...ALL_PIECES.map((p) => ({ key: `take.${p}`, phrase: `взятие ${pieceNameRu(p, 'ins')}` })),
  ...ALL_PIECES.map((p) => ({ key: `go.${p}`, phrase: `ход ${pieceNameRu(p, 'ins')}` })),
];

/** «Мой совет был такой: ход слоном.» — the reminders after «Стоп-стоп…» / «И этот ход теряет …». */
export const HELPER_ADVICE_REMINDERS: readonly ((advice: string) => string)[] = [(a) => `Мой совет был такой: ${a}.`, (a) => `Помнишь мой совет: ${a}?`];

const ADVICE: readonly ClipCatalogLine[] = HELPER_ADVICE_PHRASES.map((a): ClipCatalogLine => ({ id: `helper.takeback.advice.${a.key}`, role: 'whole', wordings: w(...HELPER_ADVICE_REMINDERS.map((r) => r(a.phrase))) }));

export const HELPER_CATALOG: readonly ClipCatalogLine[] = [...HINT_L1, ...HINT_L2, ...HINT_L3, ...MOVES, ...HINT_L4, ...EXPLAIN, ...THREATS, ...ROUTINE, ...OPENINGS, ...ADVICE];
